import { useContext, useEffect, useMemo, useState } from "react";
import { Button, Card, CardBody, Col, Container, FormGroup, Row } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";

import { validate } from "components/validateGeneralForm";
import TitleFormWithEdit from "components/TitleFormWithEdit";
import GeneralForm from "components/GeneralForm";
import Error from "components/Error";
import usuarioService from "services/UsuarioService";
import comboService from "services/ComboService";
import { erroInesperado } from "services/Utils";
import { toast } from "react-toastify";
import { PapelEnum } from "services/PerfilService";
import AppContext from "AppContext";
import { confirm } from "components/Confirmation";

export default function FormUsuario() {
  const { state } = useContext(AppContext)
  const idTipoPerfil = state?.user?.perfil?.idTipo

  const [data, setData] = useState({})
  const [edit, setEdit] = useState(false)
  const [perfis, setPerfis] = useState([])
  const [empresas, setEmpresas] = useState([])
  const [fiscais, setFiscais] = useState([])
  const [gestoresContrato, setGestoresContrato] = useState([])
  const params = useParams()
  const navigate = useNavigate()

  const id = parseInt(params.id)
  const novo = (0 === id)

  useEffect(() => {
    comboService.perfil().then(res => {
      if (res.success) {
        setPerfis(res.data)
      }
    })

    if (idTipoPerfil == PapelEnum.ADMIN) {
      comboService.empresa().then(res => {
        if (res.success) {
          setEmpresas(res.data)
        }
      })
    }

    comboService.fiscal().then(res => {
      if (res.success) {
        setFiscais(res.data)
      }
    })

    comboService.gestorContrato().then(res => {
      if (res.success) {
        setGestoresContrato(res.data)
      }
    })
  }, [])

  // papeis subordinados a um superior (gravado em idSuperior do vinculo): Agente do Fiscal
  // (FISCAL_CAMPO, superior = Fiscal) e Assistente do Gestor de Contrato (SDD 044, superior =
  // Gestor de Contrato). Retorna label/opcoes do combo de superior, ou null se o tipo nao exige.
  const configSuperior = (idTipo) => {
    if (idTipo == PapelEnum.FISCAL_CAMPO)
      return { label: 'Fiscal', opcoes: fiscais }
    if (idTipo == PapelEnum.ASSISTENTE_GESTOR_CONTRATO)
      return { label: 'Gestor de Contrato', opcoes: gestoresContrato }
    return null
  }

  useEffect(() => {
    if (!!id) {
      usuarioService.getById(id).then(res => {
        if (res.success) {
          // SDD 034 - papeis[] vem do backend no formato de exibicao (nomePerfil/razaoSocial/
          // nomeSuperior inclusos), mas os campos usados pra editar sao os mesmos idPerfil/
          // idEmpresa/idSuperior de antes - mantem o objeto inteiro (os campos extras nao
          // atrapalham, so nao sao reenviados no handleSubmit).
          setData({
            ...res.data,
            papeis: (res.data.papeis && res.data.papeis.length > 0) ? res.data.papeis : [{}]
          })
        }
      })
    } else {
      setData({ papeis: [{}] })
    }
  }, [id])

  useEffect(() => {
    if (novo) {
      setEdit(true)
    }
  }, [novo])

  useEffect(() => {
    if (!novo) {
      // carrega os itens

      // busca o valor final atual das OSs
    }
  }, [novo])

  // Engenheiro (GESTOR_EMPRESA) só gerencia usuários da própria empresa, e só EXECUTOR/GESTOR_EMPRESA
  // tem empresa (ver TIPOS_QUE_EXIGEM_EMPRESA no backend) — restringe as opções pra bater com o que o
  // backend de fato aceita, em vez de deixar escolher um perfil que vai ser rejeitado ao salvar.
  const perfisElegiveis = useMemo(() => {
    if (idTipoPerfil == PapelEnum.ADMIN)
      return perfis
    return perfis.filter(el => el.idTipo == PapelEnum.EXECUTOR || el.idTipo == PapelEnum.GESTOR_EMPRESA)
  }, [perfis, idTipoPerfil])

  // SDD 034 - o form deixa de ter 1 unico bloco de campos condicionais (idPerfil unico + campos
  // dependentes) e passa a ter só os campos fixos de identificação aqui; a lista de papéis (N
  // blocos repetíveis, cada um com sua própria lógica condicional) é renderizada à parte
  // (ver seção "Papéis" no JSX), fora do GeneralForm/validate genéricos - a SDD deixa em aberto
  // se o GeneralForm suporta listas nativamente e não suporta, então a seção é manual.
  const formConfig = useMemo(() => [
    [
      { name: "nome", label: 'Nome', type: "text", required: true, colspan: 6 },
      { name: "email", label: 'E-mail', type: "text", required: true, colspan: 6 },
      { name: "telefone", label: 'Telefone', type: "phone", required: true, colspan: 4 },
    ],
  ], [])

  const handleChange = (field, value) => {
    setData({ ...data, [field]: value })
  }

  const papeis = data.papeis || [{}]

  // Decisao de escopo 6 (SDD 034) - cada linha exclui, do combo de Perfil, os idTipo já
  // escolhidos nas OUTRAS linhas da própria lista (regra de "1 papel por tipo" do backend,
  // replicada aqui pra não deixar montar um payload que vai ser rejeitado ao salvar).
  const perfisElegiveisParaLinha = (idx) => {
    const idsTipoUsadosOutrasLinhas = new Set(
      papeis
        .filter((_, i) => i !== idx)
        .map(p => perfisElegiveis.find(el => el.id === parseInt(p.idPerfil))?.idTipo)
        .filter(el => el !== undefined)
    )
    return perfisElegiveis.filter(el => !idsTipoUsadosOutrasLinhas.has(el.idTipo))
  }

  const handleChangePapel = (idx, field, value) => {
    const novosPapeis = [...papeis]
    novosPapeis[idx] = { ...novosPapeis[idx], [field]: value }
    // ao trocar o perfil da linha, limpa os campos dependentes (empresa/fiscal) que possam
    // não fazer mais sentido pro novo perfil escolhido
    if (field === 'idPerfil') {
      novosPapeis[idx].idEmpresa = null
      novosPapeis[idx].idSuperior = null
    }
    setData({ ...data, papeis: novosPapeis })
  }

  const handleAddPapel = () => {
    setData({ ...data, papeis: [...papeis, {}] })
  }

  const handleRemovePapel = (idx) => {
    const novosPapeis = papeis.filter((_, i) => i !== idx)
    setData({ ...data, papeis: novosPapeis.length > 0 ? novosPapeis : [{}] })
  }

  // a remocao so e efetivada de fato no backend ao salvar - se o backend rejeitar (ex.: papel
  // com dependencia pendente), a linha ja some da tela e so volta com F5. Confirma antes pra
  // reduzir o risco de excluir o papel errado sem querer.
  const handleClickRemoverPapel = (idx, papel) => {
    const perfil = perfisElegiveis.find(el => el.id === parseInt(papel.idPerfil))
    const texto = perfil
      ? `Tem certeza que deseja remover o papel "${perfil.nome}"?`
      : 'Tem certeza que deseja remover este papel?'
    confirm({
      header: 'Remover papel',
      text: texto,
      onConfirm: () => handleRemovePapel(idx),
    })
  }

  const validarPapeis = () => {
    const errors = []
    papeis.forEach((papel, idx) => {
      if (!papel.idPerfil) {
        errors.push({ fieldName: `papeis.${idx}.idPerfil`, error: 'Campo obrigatório' })
        return
      }
      const perfil = perfisElegiveis.find(el => el.id === parseInt(papel.idPerfil))
      if (!perfil)
        return

      if (perfil.selecionarEmpresa && idTipoPerfil == PapelEnum.ADMIN && !papel.idEmpresa) {
        errors.push({ fieldName: `papeis.${idx}.idEmpresa`, error: 'Campo obrigatório' })
      }
      if (configSuperior(perfil.idTipo) && !papel.idSuperior) {
        errors.push({ fieldName: `papeis.${idx}.idSuperior`, error: 'Campo obrigatório' })
      }
    })
    return errors
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const errors = [...validate(formConfig, data), ...validarPapeis()]
    if (errors.length > 0) {
      setData({ ...data, errors })
      // erros foram encontrados
      return
    }

    const payload = {
      nome: data.nome,
      email: data.email,
      telefone: data.telefone,
      papeis: papeis.map(p => ({
        idPerfil: p.idPerfil ? +p.idPerfil : undefined,
        idEmpresa: p.idEmpresa ? +p.idEmpresa : undefined,
        idSuperior: p.idSuperior ? +p.idSuperior : undefined,
      }))
    }

    try {
      if (novo) {
        await usuarioService.create(payload)
      } else {
        await usuarioService.update(id, payload)
      }
      toast('Registro salvo com sucesso!')
      navigate('/admin/usuarios')
    } catch (ex) {
      // Decisao 9 (SDD 034) - erros de validacao de papeis (unicidade por tipo, limite de app,
      // dependencia pendente ao remover) vem do backend como 400/409/422 com `message` pronta
      // pra exibir - sem tentar replicar 100% da regra no client (dependencias so o backend sabe).
      if (ex.response?.data?.message) {
        toast(ex.response.data.message)
        return
      }
      erroInesperado()
    }
  }

  return (
    <Container fluid>
      <TitleFormWithEdit title="Usuários" edit={edit} onChangeEdit={setEdit} showButton={!novo} />
      <Card>
        <CardBody>
          <form onSubmit={handleSubmit}>

            <GeneralForm
              config={formConfig}
              data={data}
              onChange={handleChange}
              disabled={!edit}
            />

            <hr />
            <h3>Papéis</h3>
            {papeis.map((papel, idx) => {
              const perfisDisponiveis = perfisElegiveisParaLinha(idx)
              const perfilSelecionado = perfisElegiveis.find(el => el.id === parseInt(papel.idPerfil))
              const exigeEmpresa = perfilSelecionado?.selecionarEmpresa && idTipoPerfil == PapelEnum.ADMIN
              const superior = configSuperior(perfilSelecionado?.idTipo)

              return (
                <Row key={idx} className="align-items-start border-top pt-3 mt-2">
                  <Col md="4">
                    <FormGroup>
                      <label>Perfil</label>
                      <select
                        className="form-control"
                        value={papel.idPerfil || ''}
                        disabled={!edit}
                        onChange={e => handleChangePapel(idx, 'idPerfil', e.target.value ? +e.target.value : null)}
                      >
                        <option value="">Selecione</option>
                        {perfisDisponiveis.map(el => (
                          <option key={el.id} value={el.id}>{el.nome}</option>
                        ))}
                      </select>
                      <Error errors={data.errors || []} fieldName={`papeis.${idx}.idPerfil`} className="mt-1" />
                    </FormGroup>
                  </Col>
                  {exigeEmpresa && (
                    <Col md="4">
                      <FormGroup>
                        <label>Empresa</label>
                        <select
                          className="form-control"
                          value={papel.idEmpresa || ''}
                          disabled={!edit}
                          onChange={e => handleChangePapel(idx, 'idEmpresa', e.target.value ? +e.target.value : null)}
                        >
                          <option value="">Selecione</option>
                          {empresas.map(el => (
                            <option key={el.id} value={el.id}>{el.razaoSocial}</option>
                          ))}
                        </select>
                        <Error errors={data.errors || []} fieldName={`papeis.${idx}.idEmpresa`} className="mt-1" />
                      </FormGroup>
                    </Col>
                  )}
                  {superior && (
                    <Col md="4">
                      <FormGroup>
                        <label>{superior.label}</label>
                        <select
                          className="form-control"
                          value={papel.idSuperior || ''}
                          disabled={!edit}
                          onChange={e => handleChangePapel(idx, 'idSuperior', e.target.value ? +e.target.value : null)}
                        >
                          <option value="">Selecione</option>
                          {superior.opcoes.map(el => (
                            <option key={el.id} value={el.id}>{el.nome}</option>
                          ))}
                        </select>
                        <Error errors={data.errors || []} fieldName={`papeis.${idx}.idSuperior`} className="mt-1" />
                      </FormGroup>
                    </Col>
                  )}
                  {edit && (
                    <Col md="auto" className="d-flex align-items-center" style={{ height: 71 }}>
                      <Button
                        type="button"
                        color="danger"
                        outline
                        size="sm"
                        disabled={papeis.length <= 1}
                        onClick={() => handleClickRemoverPapel(idx, papel)}
                      >
                        Remover
                      </Button>
                    </Col>
                  )}
                </Row>
              )
            })}
            {edit && (
              <div className="mt-2 mb-4">
                <Button type="button" color="secondary" size="sm" onClick={handleAddPapel}>Adicionar papel</Button>
              </div>
            )}

            {edit && (
              <div className="text-right">
                <Button type="submit" color="primary">Salvar</Button>
              </div>
            )}
          </form>
        </CardBody>
      </Card>

    </Container>
  )
}